import React, { useEffect, useRef, useState } from 'react';
import { shortFilmData } from '../data/mock';
import { Play, Film, Instagram, Youtube, ExternalLink } from 'lucide-react';

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

export default function ShortFilmBanner() {
  const sectionRef = useRef(null);
  const visible = useInView(sectionRef);

  return (
    <section ref={sectionRef} className="relative overflow-hidden">
      <div className="section-container">
        <div className={`fade-in-up ${visible ? 'visible' : ''}`}>
          {/* Banner card */}
          <div className="relative rounded-2xl overflow-hidden border border-[#00e5ff]/20 bg-[#0c1017]">
            {/* Background image with heavy overlay */}
            <div className="absolute inset-0">
              <img
                src={shortFilmData.thumbnail}
                alt=""
                className="w-full h-full object-cover opacity-20"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#06080d] via-[#06080d]/90 to-[#06080d]/70" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06080d] via-transparent to-[#06080d]/50" />
            </div>

            {/* Scanlines */}
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,229,255,0.03) 3px, rgba(0,229,255,0.03) 6px)'
            }} />

            {/* Content */}
            <div className="relative z-10 p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12">
              {/* Left - Play button */}
              <div className="flex-shrink-0 relative group cursor-pointer">
                <div className="w-28 h-28 rounded-full border-2 border-[#00e5ff]/40 flex items-center justify-center bg-[#00e5ff]/5 group-hover:bg-[#00e5ff]/15 group-hover:border-[#00e5ff]/60 transition-all duration-500 group-hover:scale-105">
                  <div className="w-20 h-20 rounded-full border border-[#00e5ff]/30 flex items-center justify-center">
                    <Play className="w-8 h-8 text-[#00e5ff] ml-1" fill="currentColor" />
                  </div>
                </div>
                {/* Pulse rings */}
                <div className="absolute inset-0 rounded-full border border-[#00e5ff]/20 animate-ping" style={{ animationDuration: '3s' }} />
              </div>

              {/* Center - Info */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                  <Film className="w-4 h-4 text-[#00e5ff]" />
                  <span className="font-['JetBrains_Mono'] text-[11px] tracking-[3px] uppercase text-[#00e5ff]">
                    {shortFilmData.subtitle}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#f1f5f9] mb-3 tracking-tight">
                  {shortFilmData.title}
                </h3>
                <p className="text-sm text-[#94a3b8] leading-relaxed max-w-lg mb-6">
                  {shortFilmData.description}
                </p>

                {/* Watch buttons */}
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                  <a
                    href={shortFilmData.youtubeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#00e5ff] text-[#06080d] font-['JetBrains_Mono'] text-xs font-semibold tracking-wider rounded-md hover:shadow-[0_0_25px_rgba(0,229,255,0.3)] transition-all duration-300 hover:translate-y-[-1px]"
                  >
                    <Youtube className="w-4 h-4" /> Watch on YouTube
                  </a>
                  <a
                    href={shortFilmData.instagramLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-2.5 border border-[#00e5ff]/40 text-[#00e5ff] font-['JetBrains_Mono'] text-xs font-medium tracking-wider rounded-md hover:bg-[#00e5ff]/10 transition-all duration-300"
                  >
                    <Instagram className="w-4 h-4" /> Instagram Reel
                  </a>
                </div>
              </div>

              {/* Right - Film strip decoration (desktop only) */}
              <div className="hidden xl:flex flex-col gap-1.5 opacity-30">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-sm bg-[#00e5ff]/40" />
                    <div className="w-16 h-3 rounded-sm bg-[#00e5ff]/20" />
                    <div className="w-2 h-2 rounded-sm bg-[#00e5ff]/40" />
                  </div>
                ))}
              </div>
            </div>

            {/* Corner accents */}
            <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-[#00e5ff]/30" />
            <div className="absolute top-4 right-4 w-8 h-8 border-t border-r border-[#00e5ff]/30" />
            <div className="absolute bottom-4 left-4 w-8 h-8 border-b border-l border-[#00e5ff]/30" />
            <div className="absolute bottom-4 right-4 w-8 h-8 border-b border-r border-[#00e5ff]/30" />
          </div>
        </div>
      </div>
    </section>
  );
}
